import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Spacing, Text } from '@reservamos/elements';
import { useTranslation } from 'react-i18next';
import InlineInput from '../../../ui/molecules/InlineInput';
import { formatCurrency } from '../../../utils/currency';
import useCurrencyConfig from '../../../hooks/whitelabel/useCurrencyConfig';

/**
 * CheckoutWalletInput component.
 * Render an input field to specify a custom amount to redeem from wallet balance during checkout.
 *
 * @param {object} props - setUsingWallet, loadingWallet, usableBalance, purchaseToken, checkboxContext.
 * @param {Function} props.setUsingWallet - Callback function to set wallet usage with purchase token and specified amount.
 * @param {boolean} props.loadingWallet - Indicates if wallet information is currently being loaded.
 * @param {number} props.usableBalance - Available wallet balance that can be redeemed.
 * @param {string} props.purchaseToken - Unique token identifying the current purchase session.
 * @param {Function} props.checkboxContext - Function to determine display context based on loading and usage states.
 * @returns {JSX.Element} The rendered wallet input component.
 */
const CheckoutWalletInput = ({
  setUsingWallet,
  loadingWallet,
  usableBalance,
  purchaseToken,
  checkboxContext,
}) => {
  const [amountToRedeem, setAmountToRedeem] = useState('');
  const { t } = useTranslation('loyalty');
  const { currency, locale } = useCurrencyConfig();

  /**
   * Submit the specified wallet balance amount for redemption.
   * Calls setUsingWallet with the current purchase token and entered amount.
   */
  const submitBalance = () => {
    setUsingWallet(purchaseToken, true, amountToRedeem);
  };

  /**
   * Handle input value changes with validation.
   * Validates that the input is a valid number within the usable balance range.
   *
   * @param {Event} e - The input change event.
   */
  const onInputChange = (e) => {
    const { value } = e.target;
    if (!/[0-9]+\.{0,1}?[0-9]*/.test(value)) {
      setAmountToRedeem('');
      return;
    }

    const valueAsNumber = Number.parseFloat(value);
    if (valueAsNumber < 0 || valueAsNumber > usableBalance) {
      setAmountToRedeem('');
      return;
    }

    setAmountToRedeem(value);
  };

  return (
    <Spacing vertical size="S">
      <Text>
        {t('wallet_input_label', {
          amount: formatCurrency(usableBalance, 2, currency, locale),
          context: checkboxContext(loadingWallet, false),
        })}
      </Text>
      <InlineInput
        name="redeem-wallet"
        placeholder={formatCurrency(usableBalance, 2, currency, locale)}
        onClick={submitBalance}
        isLoading={loadingWallet}
        setAmountToRedeem={setAmountToRedeem}
        usableBalance={usableBalance}
        value={amountToRedeem}
        onChange={onInputChange}
      />
    </Spacing>
  );
};

CheckoutWalletInput.propTypes = {
  checkboxContext: PropTypes.func.isRequired,
  setUsingWallet: PropTypes.func.isRequired,
  loadingWallet: PropTypes.bool.isRequired,
  usableBalance: PropTypes.number.isRequired,
  purchaseToken: PropTypes.string.isRequired,
};

export default CheckoutWalletInput;
